import {SourceInspectorPanels} from '@waka/compat/SourceInspectorPanels';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { scrollbarStyle } from '../constants';
import { StateInitializedContext } from '../context-provider';
import {
  useAllItems,
  useItems,
  useSelectedItems,
  useTransitions,
  useWriteContext,
} from '../utils/use-context';
import { CompositionInspector } from './composition-inspector';
import { InspectorContent } from './inspector-content';
import { GroupInspector } from './group-inspector';
import { AnimationAddEnabledProvider } from './controls/animation-add-enabled-context';
import { exitToGroup } from '../state/actions/select-group';
import { setActiveSelectionSurface } from '../state/actions/set-active-selection-surface';
import { useInspectorScrollRestoration } from './scroll-restoration';
import { MovieFill, ChevronLeft } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { SettingPanel } from '@/features/brand-template/ui/panels';
import EditorHeaderDebugItemsButton from '@/features/remotion-editor/editor/header/EditorHeaderDebugItemsButton';
import type { EditorStarterItem } from '../items/item-type';
import { CollapseExpandButton } from './collapse-expand-button';
import { useCollapseExpandAll } from './use-collapse-expand-all';

export const INSPECTOR_WIDTH = 400;

const ITEM_TYPE_KEY_MAP: Record<string, string> = {
  captions: 'caption',
  video: 'clip',
  solid: 'shape',
};

// Editor.inspector.item-type 에 실제로 존재하는 키. 여기 없는 타입이 오면 next-intl 이
// MISSING_MESSAGE 로 크래시하므로 'default'("아이템")로 폴백한다.
const KNOWN_ITEM_TYPE_KEYS = new Set([
  'caption',
  'text',
  'title',
  'clip',
  'image',
  'audio',
  'shape',
  'blur',
  'gif',
  'webp',
  'pdf',
  'ppt',
  'code',
  'group',
]);

const getItemTypeI18nKey = (item: EditorStarterItem): string => {
  if (item.type === 'text') {
    if (item.renderMeta?.clipType === 'caption') return 'caption';
    if (item.renderMeta?.clipType === 'title') return 'text';
    return 'text';
  }
  const key = ITEM_TYPE_KEY_MAP[item.type] ?? item.type;
  return KNOWN_ITEM_TYPE_KEYS.has(key) ? key : 'default';
};

const MultiSelectionSummary: React.FC<{ itemIds: string[] }> = ({
  itemIds,
}) => {
  const t = useTranslations();
  const items = useItems(itemIds);
  const { transitions } = useTransitions();

  const regularItems = useMemo(
    () => items.filter((item) => !(item.id in transitions)),
    [items, transitions],
  );

  if (regularItems.length <= 1) return null;

  const primaryItem = regularItems[0];
  if (!primaryItem) return null;

  const hasMixedTypes = regularItems.some(
    (item) => item.type !== primaryItem.type,
  );
  if (hasMixedTypes) return null;

  const typeKey = getItemTypeI18nKey(primaryItem);
  const typeLabel = t(`Editor.inspector.item-type.${typeKey}`);

  return (
    <div className="w-full px-sm py-2xs bg-grad-linear-neutral-light-default">
      <div className="flex justify-center">
        <div className="pointer-events-auto rounded-2xs bg-sys-style-neutral-lightest px-sm py-2xs text-body-small">
          <span className="text-title-medium text-sys-cont-primary-default">
            {t('Editor.inspector.multi-selection-count', {
              type: typeLabel,
              count: regularItems.length,
            })}
          </span>
          <span className="text-sys-cont-neutral-default">
            {' '}
            {t('Editor.inspector.multi-selection-suffix')}
          </span>
        </div>
      </div>
    </div>
  );
};

export const Inspector: React.FC = () => {
  const t = useTranslations();
  const { selectedItems, activeGroupId, inspectedGroupMemberId } = useSelectedItems();
  const { items } = useAllItems();
  const { setState } = useWriteContext();
  const initialized = useContext(StateInitializedContext);

  // 그룹 컨텍스트 분기:
  // - 그룹 전체 선택(멤버 2개 이상): 멤버 목록(GroupInspector).
  // - 그룹 멤버 depth(멤버 1개): "← 그룹으로" 바 + 그 멤버 inspector.
  const isGroupBlock = activeGroupId != null && selectedItems.length > 1;
  const isGroupMemberDepth =
    activeGroupId != null && selectedItems.length === 1;

  // 구세대 groupId 그룹(activeGroupId 가 아이템 id 가 아님)의 멤버 depth 에서는 애니 추가를
  // 막는다 — 거긴 멤버 animations 가 그룹 애니로 승격돼 하나만 붙여도 카드 전체가 움직인다
  // (canvas/group-layer.tsx 관례). 1급 GroupItem 멤버는 그대로 추가 가능.
  const isAnimationAddEnabled =
    !isGroupMemberDepth ||
    (activeGroupId != null && items[activeGroupId]?.type === 'group');

  // 선택 전환(그룹으로 복귀)은 편집이 아니므로 undo 스택에 쌓지 않는다.
  const handleBackToGroup = useCallback(() => {
    setState({
      update: (state) =>
        setActiveSelectionSurface(exitToGroup(state), 'canvas'),
      commitToUndoStack: false,
    });
  }, [setState]);
  const {
    isAllCollapsed: isProjectAllCollapsed,
    collapseAllTrigger: projectCollapseAllTrigger,
    expandAllTrigger: projectExpandAllTrigger,
    handleToggleAll: handleToggleProjectSections,
  } = useCollapseExpandAll();

  const ref = useRef<HTMLDivElement>(null);

  useInspectorScrollRestoration(ref, selectedItems);

  const style: React.CSSProperties = useMemo(() => {
    return {
      ...scrollbarStyle,
      width: INSPECTOR_WIDTH,
    };
  }, []);

  return (
    <div
      className="relative h-full w-[400px] overflow-y-auto border-l-[1px] border-l-sys-style-neutral-default bg-sys-bg-surface text-sys-cont-neutral-default"
      style={style}
      ref={ref}
    >
      {initialized&&<SourceInspectorPanels item={items[inspectedGroupMemberId??(selectedItems.length===1?selectedItems[0]:'')]}/>}
      {isGroupBlock && <GroupInspector memberIds={selectedItems} />}
      {isGroupMemberDepth && (
        <>
          <button
            type="button"
            onClick={handleBackToGroup}
            className="flex w-full items-center gap-2xs border-b border-sys-style-neutral-light px-sm py-2xs text-body-small text-sys-cont-primary-default transition-colors hover:bg-sys-bg-surface-hover focus-visible:outline-none"
          >
            <ChevronLeft className="size-4" />
            {t('Editor.inspector.group.back-button')}
          </button>
          <AnimationAddEnabledProvider isEnabled={isAnimationAddEnabled}>
            <InspectorContent itemIds={selectedItems} />
          </AnimationAddEnabledProvider>
        </>
      )}
      {activeGroupId == null && selectedItems.length > 0 && (
        <InspectorContent itemIds={selectedItems} />
      )}
      {selectedItems.length === 0 && initialized && (
        <SettingPanel
          icon={<MovieFill className="size-[20px]" />}
          title={t('Editor.inspector.composition.title')}
          trailingNode={
            <CollapseExpandButton
              isAllCollapsed={isProjectAllCollapsed}
              onToggle={handleToggleProjectSections}
            />
          }
        >
          <CompositionInspector
            showLayoutChange={false}
            collapseAllTrigger={projectCollapseAllTrigger}
            expandAllTrigger={projectExpandAllTrigger}
            sectionStateKey="editor-project-inspector"
          />
        </SettingPanel>
      )}
      <div
        className={`pointer-events-none sticky z-10 ${selectedItems.length > 1 ? 'bottom-0' : 'bottom-sm'}`}
      >
        <div
          className={`flex justify-end px-sm ${selectedItems.length > 1 ? 'pb-2xs' : ''}`}
        >
          <div className="pointer-events-auto">
            <EditorHeaderDebugItemsButton />
          </div>
        </div>
        {selectedItems.length > 1 && !isGroupBlock && (
          <MultiSelectionSummary itemIds={selectedItems} />
        )}{' '}
      </div>
    </div>
  );
};
